import { TypedDocumentNode, gql } from '@apollo/client';
import { faIndustry } from '@fortawesome/pro-solid-svg-icons';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { VIDEO_FORMAT, Video } from '@sorare/core/src/atoms/ui/Video';
import { FOOTBALL_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import {
  ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE,
  ANY_SPORT_PLAY_INVENTORY_SHARDS_TYPE,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Factory_currentUser } from './__generated__/index.graphql';
import bgMobile from './assets/bg-mobile.jpg';
import bgVideo from './assets/bg.mp4';
import box from './assets/box.png';
import earlyAccess from './assets/earlyAccess.png';
import poster from './assets/poster.jpg';
import shards from './assets/shards.png';

const Root = styled(LinkBox)`
  padding: var(--intermediate-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-neutral-200);
  background-position: center;
  background-size: cover;
  background-image: url(${bgMobile});
  position: relative;
  overflow: hidden;
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  align-items: start;
  flex-direction: column;
  position: relative;

  > *:nth-child(2) {
    align-self: center;
  }

  @media ${laptopAndAbove} {
    align-items: start;
    flex-direction: row;
  }
`;
const Block = styled.div`
  width: calc(10 * var(--unit));
  height: calc(10 * var(--unit));
  border-radius: var(--unit);
  border: 2px solid var(--c-brand-600);
  background: radial-gradient(
    50% 50% at 50% 50%,
    var(--c-neutral-400) 0%,
    var(--c-neutral-100) 100%
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    aspect-ratio: 1;
    object-fit: contain;
  }

  &:nth-child(1) {
    img {
      width: 90%;
    }
  }
  &:nth-child(2) {
    img {
      width: 70%;
    }
  }

  ${BodyS} {
    position: absolute;
    bottom: var(--half-unit);
    right: var(--half-unit);
  }
`;
const StyledVideo = styled(Video)`
  position: absolute;
  inset: 0;
`;

export type Props = { currentUser: Factory_currentUser };

export const Factory = ({ currentUser }: Props) => {
  const [videoStarted, setVideoStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { sport, generateSportContextPath } = useSportContext();

  const totalBoxes = currentUser.factoryBundles.totalCount;

  const totalShards = currentUser.factoryShardsChests.reduce(
    (acc, chest) => acc + chest.cardShardsCount,
    0
  );

  const totalBlueprints =
    currentUser.anyUserSportProfile?.blueprintCardsCount || 0;

  return (
    <Root>
      {isLaptopAndAbove && (
        <StyledVideo
          onMouseEnter={() => {
            if (!videoStarted) {
              videoRef.current?.play();
              setVideoStarted(true);
            }
          }}
          ref={videoRef}
          sources={[{ src: bgVideo, type: VIDEO_FORMAT.MP4 }]}
          muted
          playsInline
          poster={poster}
          hideMutedIcon
        />
      )}
      <StyledSBHorizontal>
        <Horizontal>
          <FontAwesomeIcon icon={faIndustry} />
          <BodyM as="p" bold>
            <FormattedMessage
              id="Profile.Factory.SorareFactory"
              defaultMessage="Sorare Factory"
            />
          </BodyM>
        </Horizontal>
        <Horizontal gap={3}>
          {/*
            TODO [US-752]:
            Update this to work cross sports once blueprint cards are available cross sports
          */}
          {sport === Sport.FOOTBALL && (
            <LinkOther
              as={ButtonBase}
              to={generatePath(FOOTBALL_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE, {
                type: Rarity.limited.toString(),
              })}
            >
              <Block>
                <img src={earlyAccess} alt="" />
                <BodyS as="p">
                  {totalBlueprints === 100 ? '100+' : totalBlueprints}
                </BodyS>
              </Block>
            </LinkOther>
          )}

          <LinkOther
            as={ButtonBase}
            to={generateSportContextPath(ANY_SPORT_PLAY_INVENTORY_SHARDS_TYPE, {
              params: {
                type: Rarity.limited.toString(),
              },
            })}
          >
            <Block>
              <img src={shards} alt="" />
              <BodyS as="p">{totalShards}</BodyS>
            </Block>
          </LinkOther>
          <LinkOverlay
            as={ButtonBase}
            to={generateSportContextPath(ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE, {
              params: {
                type: Rarity.limited.toString(),
              },
            })}
          >
            <Block>
              <img src={box} alt="" />
              <BodyS as="p">{totalBoxes}</BodyS>
            </Block>
          </LinkOverlay>
        </Horizontal>
      </StyledSBHorizontal>
    </Root>
  );
};

Factory.fragments = {
  currentUser: gql`
    fragment Factory_currentUser on CurrentUser {
      slug
      factoryShardsChests: cardShardsChests(sport: $sport) {
        id
        rarity
        cardShardsCount
      }
      factoryBundles: unopenedProbabilisticBundles(sport: $sport, first: 100) {
        totalCount
      }
      anyUserSportProfile(sport: $sport) {
        id
        blueprintCardsCount
      }
    }
  ` as TypedDocumentNode<Factory_currentUser>,
};
